import React, { useState } from 'react'

const SectionTitle = ({ children }) => (
    <h6 style={{ borderBottom: '1px solid #e0e0e0', paddingBottom: '8px', marginTop: '24px', marginBottom: '12px' }}>
        {children}
    </h6>
)

const DetailRow = ({ label, value }) => (
    <div style={{ display: 'flex', padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
        <span style={{ fontWeight: 500, width: '200px', flexShrink: 0, color: '#555' }}>{label}</span>
        <span style={{ color: 'var(--neutral-900)' }}>{value ?? '-'}</span>
    </div>
)

const formatCount = (value) => {
    if (value === null || value === undefined || value === '') return 'No data'
    const n = Number(value)
    return Number.isFinite(n) ? String(Math.round(n)) : String(value)
}

const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

const formatAddress = (loc) => {
    if (!loc) return '-'
    return [loc.addressLine1, loc.addressLine2, loc.city, loc.state, loc.zipCode, loc.country].filter(Boolean).join(', ')
}

// Turn a raw upstream error into a plain-English explanation + what the user can do next.
const explainError = (err, dotNumber) => {
    const msg = (err?.message || '').toLowerCase()
    const http = err?.httpCode
    const dot = dotNumber || ''

    if (msg.includes('numeric') || msg.includes('dotnumber') || msg.includes('usdot')) {
        return {
            title: 'That doesn’t look like a valid DOT number',
            body: `We couldn’t look up “${dot}”. USDOT numbers are 6–7 digits and contain only numbers. Double-check the number and try again.`,
        }
    }
    if (http === 404 || msg.includes('not found')) {
        return {
            title: 'No carrier found for that DOT number',
            body: `USDOT “${dot}” isn’t in the FMCSA registry. Confirm the number with the carrier and try again.`,
        }
    }
    if (http === 429 || msg.includes('rate limit') || msg.includes('too many')) {
        return {
            title: 'Motus is rate-limiting requests',
            body: 'Please wait a moment and try again.',
        }
    }
    if (http >= 500) {
        return {
            title: 'Motus is temporarily unavailable',
            body: 'The upstream carrier service is having trouble. Try again in a few minutes.',
        }
    }
    return {
        title: 'This search didn’t return any data',
        body: 'Motus rejected the request. Check the number and try again — see technical details below if it keeps happening.',
    }
}

const SearchFailedCard = ({ data }) => {
    const [showDetails, setShowDetails] = useState(false)
    const err = data.error || {}
    const { title, body } = explainError(err, data.dotNumber)

    return (
        <div style={{ border: '1px solid #f5c6cb', background: 'var(--badge-declined-bg)', color: 'var(--badge-declined-ink)', padding: 20, borderRadius: 6 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <span style={{ fontSize: 24, lineHeight: 1 }}>⚠️</span>
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>{title}</div>
                    <div style={{ fontSize: 14, lineHeight: 1.5 }}>{body}</div>

                    <button
                        onClick={() => setShowDetails((v) => !v)}
                        style={{
                            marginTop: 16,
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--badge-declined-ink)',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            padding: 0,
                            fontSize: 13,
                        }}
                    >
                        {showDetails ? 'Hide' : 'Show'} technical details
                    </button>

                    {showDetails && (
                        <div style={{ marginTop: 12, padding: 12, background: 'rgba(0,0,0,0.04)', borderRadius: 4, fontSize: 12, fontFamily: 'monospace' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 12px' }}>
                                {data.dotNumber && (<><span>DOT number:</span><span>{data.dotNumber}</span></>)}
                                {err.httpCode != null && (<><span>HTTP status:</span><span>{err.httpCode}</span></>)}
                                {err.code && (<><span>Interaction code:</span><span>{err.code}</span></>)}
                                {err.errorCode && (<><span>Error code:</span><span>{err.errorCode}</span></>)}
                                {err.message && (<><span>Upstream message:</span><span style={{ wordBreak: 'break-all' }}>{err.message}</span></>)}
                                {err.startedAt && (<><span>Started:</span><span>{formatDate(err.startedAt)}</span></>)}
                                {err.endedAt && (<><span>Finished:</span><span>{formatDate(err.endedAt)}</span></>)}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const MotusDotDetail = ({ data }) => {
    if (!data) return null

    // Backend wraps the carrier response in { status, dotNumber, createdAt, response, error }.
    // If the search failed, we get `error` instead of `response` — show a friendly explanation.
    if (data.error) {
        return <SearchFailedCard data={data} />
    }

    // Success path: the wrapper carries the parsed carrier response under `response`. Older shape
    // (raw carrier response at the top level) is still supported for safety.
    const payload = data.response ?? data
    const dotInfo = payload.entityDotNumber
    const carrierDetail = payload.carrierEntityDetail
    const primaryLocation = payload.locations?.find(l => l.primaryAddressFlag) || payload.locations?.[0]
    const primaryEmail = payload.emailAddresses?.find(e => e.primaryAddressFlag)
    const registration = payload.entityRegistrations?.[0]

    return (
        <div>
            {/* General Info */}
            <SectionTitle>General Information</SectionTitle>
            <DetailRow label="Entity Name" value={payload.entityName} />
            <DetailRow label="DOT Number" value={dotInfo?.dotNumber} />
            <DetailRow label="DOT Status" value={dotInfo?.dotNumberStatus?.dotNumberStatus} />
            <DetailRow label="Out of Service" value={payload.outOfService ? 'Yes' : 'No'} />
            <DetailRow label="Created" value={formatDate(payload.createDate)} />
            <DetailRow label="Last Updated" value={formatDate(payload.updateDate)} />

            {/* Entity Type */}
            {payload.entityTypes?.length > 0 && (
                <>
                    <SectionTitle>Entity Type</SectionTitle>
                    {payload.entityTypes.map((et, idx) => (
                        <DetailRow key={idx} label="Type" value={et.entityTypeTitle?.entityTypeName} />
                    ))}
                </>
            )}

            {/* Carrier Details */}
            {carrierDetail && (
                <>
                    <SectionTitle>Carrier Details</SectionTitle>
                    <DetailRow label="Business Type" value={carrierDetail.businessType?.businessTypeName} />
                    <DetailRow label="OIC State" value={carrierDetail.oicState} />
                    <DetailRow label="MCS-150 Date" value={formatDate(carrierDetail.mcs150Date)} />
                    <DetailRow label="MCS-150 Mileage"
                               value={carrierDetail.mcs150Mileage ? Number(carrierDetail.mcs150Mileage).toLocaleString() : '-'} />
                    <DetailRow label="Mileage Year" value={carrierDetail.mcs150MileageYear} />
                    <DetailRow label="Total Drivers" value={formatCount(carrierDetail.driverTotal)} />
                    <DetailRow label="Interstate Drivers" value={formatCount(carrierDetail.driverInterTotal)} />
                    <DetailRow label="Power Units" value={formatCount(carrierDetail.nbrPowerUnit)} />
                    <DetailRow label="Avg Power Unit" value={formatCount(carrierDetail.avgPowerUnit)} />
                </>
            )}

            {/* Address */}
            <SectionTitle>Address</SectionTitle>
            <DetailRow label="Primary Address" value={formatAddress(primaryLocation)} />
            {primaryLocation?.county && <DetailRow label="County" value={primaryLocation.county} />}

            {/* Contact */}
            <SectionTitle>Contact</SectionTitle>
            <DetailRow label="Email" value={primaryEmail?.emailAddress} />
            {payload.phoneNumbers?.length > 0 && payload.phoneNumbers.map((p, idx) => (
                <DetailRow key={idx} label="Phone" value={p.phoneNumber || p} />
            ))}

            {/* Officers */}
            {payload.entityOfficers?.length > 0 && (
                <>
                    <SectionTitle>Officers</SectionTitle>
                    {payload.entityOfficers.map((officer, idx) => (
                        <div key={idx} style={{ marginBottom: '12px', paddingLeft: '8px', borderLeft: '3px solid #1976d2' }}>
                            <DetailRow label="Name" value={[officer.firstName, officer.middleName, officer.lastName].filter(Boolean).join(' ')} />
                            <DetailRow label="Title" value={officer.title} />
                            <DetailRow label="Phone" value={officer.phoneNumber} />
                            <DetailRow label="Email" value={officer.email} />
                        </div>
                    ))}
                </>
            )}

            {/* Other Names */}
            {payload.entityNames?.length > 1 && (
                <>
                    <SectionTitle>Other Names</SectionTitle>
                    {payload.entityNames.map((n, idx) => (
                        <DetailRow key={idx} label={n.nameType} value={n.entityName} />
                    ))}
                </>
            )}

            {/* Registration */}
            {registration && (
                <>
                    <SectionTitle>Registration</SectionTitle>
                    <DetailRow label="Interstate" value={registration.interstateChk ? 'Yes' : 'No'} />
                    <DetailRow label="Intrastate" value={registration.intrastateChk ? 'Yes' : 'No'} />
                    <DetailRow label="For Hire" value={registration.forHireChk ? 'Yes' : 'No'} />
                    <DetailRow label="Private" value={registration.privateChk ? 'Yes' : 'No'} />
                    <DetailRow label="Property" value={registration.propertyChk ? 'Yes' : 'No'} />
                    <DetailRow label="Passenger" value={registration.passengerChk ? 'Yes' : 'No'} />
                    <DetailRow label="HHG" value={registration.hhgChk ? 'Yes' : 'No'} />
                    <DetailRow label="Min Coverage" value={registration.minCovAmount ? `$${registration.minCovAmount}` : '-'} />
                    <DetailRow label="BIPD Filed" value={registration.bipdFile ? `$${registration.bipdFile}` : '-'} />
                </>
            )}
        </div>
    )
}

export default MotusDotDetail